import { Stack } from '@chakra-ui/react';

import { TaxResidency } from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import useCountriesList from 'common/countries/useCountriesList';
import Button from 'components/_core/Button';
import InformationSection, {
  InformationSectionItem,
} from 'features/Onboarding/components/InformationSection';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import VerifyInformationCallout from 'features/Onboarding/components/VerifyInformationCallout';

import Header from '../../../../components/Header';
import {
  incomeRangeChoices,
  personalAssetsChoices,
} from '../ComplianceForm/complianceProperties';
import { FormResult } from '../ComplianceForm/components/Form';
import locales from './locales';

interface ComplianceRecapProps {
  complianceValues: Partial<FormResult>;
  taxResidency: Pick<TaxResidency, 'country' | 'fiscalId'> | null;
  onNext: () => void;
}

/**
 * A page that shows a recap of the user’s compliance info
 */
const ComplianceRecap = asBentoPage<ComplianceRecapProps>(
  ({ complianceValues, Layout, onNext, onPrev, taxResidency }) => {
    const {
      hasForeignFiscalResidence,
      incomeRange,
      isSpecifiedUSPerson,
      personalAssets,
    } = complianceValues;

    const countriesOptions = useCountriesList();

    const getTaxResidencyStringValue = (): string => {
      const { country, fiscalId } = taxResidency || {};
      const countryLabel = countriesOptions.find(
        (countryOption) => countryOption.value === country,
      )?.label;

      if (!countryLabel) {
        return '';
      }

      return fiscalId ? `${countryLabel} - ${fiscalId}` : `${countryLabel}`;
    };

    const getForeignFiscalResidenceString = (): string => {
      if (hasForeignFiscalResidence === undefined) {
        return '';
      }

      if (hasForeignFiscalResidence === false) {
        return locales.labels.no;
      }

      return getTaxResidencyStringValue() || locales.labels.yes;
    };

    const getIsSpecifiedUSPersonString = (): string => {
      if (isSpecifiedUSPerson === undefined) {
        return '';
      }

      return isSpecifiedUSPerson ? locales.labels.yes : locales.labels.no;
    };

    const isSpecifiedUSPersonString = getIsSpecifiedUSPersonString();
    const foreignFiscalResidenceString = getForeignFiscalResidenceString();
    const personalAssetsString = personalAssetsChoices.find(
      (choice) => choice.value === personalAssets,
    )?.label;
    const incomeRangeString = incomeRangeChoices.find(
      (choice) => choice.value === incomeRange,
    )?.label;

    const complianceInformationItems: InformationSectionItem[] = [
      [locales.labels.assets, personalAssetsString ?? null],
      [locales.labels.income, incomeRangeString ?? null],
      [locales.labels.usCitizen, isSpecifiedUSPersonString],
      [locales.labels.fiscalResidence, foreignFiscalResidenceString],
    ];

    return (
      <Layout data-testid="compliance" nav={<OBNavigation onPrev={onPrev} />}>
        <Header title={locales.title} />

        <Stack spacing="space-40">
          <VerifyInformationCallout />
          <InformationSection
            iconName="euro"
            items={complianceInformationItems}
            title={locales.section}
          />
          <Button isExpanded onClick={onNext}>
            {locales.cta}
          </Button>
        </Stack>
      </Layout>
    );
  },
);

export default ComplianceRecap;
